/**
 * StudySync Participant Service
 * Handles all participant-related API operations
 */

import { api, authenticatedApi } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';
import type {
  Participant,
  PersonalInfo,
  VerificationSession,
  ApiResponse,
} from '../types';

export class ParticipantService {
  static async assignTrackingId(participantId: string, token: string): Promise<{ success: boolean; trackingId: string }> {
    return authenticatedApi.post(`/api/participants/${encodeURIComponent(participantId)}/tracking-id`, {}, token);
  }

  /**
   * Enroll a new participant
   */
  static async enrollParticipant(
    participantData: { 
      participantEmail: string; 
      studyCode: string;
      firstName?: string;
      lastName?: string;
      dateOfBirth?: string;
    }
  ): Promise<ApiResponse<{ participantId: string }>> {
    return api.post(API_ENDPOINTS.PARTICIPANTS.ENROLL, participantData);
  }

  /**
   * Get all participants for authenticated researcher
   */
  static async getParticipants(token: string): Promise<ApiResponse<Participant[]>> {
    return authenticatedApi.get<ApiResponse<Participant[]>>(
      API_ENDPOINTS.PARTICIPANTS.LIST,
      token
    );
  }

  /**
   * Get a specific participant by ID
   */
  static async getParticipant(
    participantId: string,
    token: string
  ): Promise<ApiResponse<Participant>> {
    return authenticatedApi.get<ApiResponse<Participant>>(
      API_ENDPOINTS.PARTICIPANTS.GET(participantId),
      token
    );
  }

  /**
   * Update participant information
   */
  static async updateParticipant(
    participantId: string,
    updates: Partial<Participant>,
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.put(
      API_ENDPOINTS.PARTICIPANTS.UPDATE(participantId),
      updates,
      token
    );
  }

  /**
   * Delete a participant
   */
  static async deleteParticipant(
    participantId: string,
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.delete(
      API_ENDPOINTS.PARTICIPANTS.DELETE(participantId),
      token
    );
  }

  static async getActiveDeletionJobs(
    studyCode: string,
    token: string
  ): Promise<ApiResponse & { jobs?: any[] }> {
    return authenticatedApi.get<ApiResponse & { jobs?: any[] }>(
      API_ENDPOINTS.PARTICIPANTS.DELETION_JOBS(studyCode),
      token
    );
  }

  /**
   * Verify participant identity
   */
  static async verifyParticipant(
    participantId: string,
    email: string,
    token?: string
  ): Promise<ApiResponse<Participant>> {
    const data = { participantId, email };
    if (token) {
      return authenticatedApi.post(API_ENDPOINTS.PARTICIPANTS.VERIFY, data, token);
    }
    return api.post(API_ENDPOINTS.PARTICIPANTS.VERIFY, data);
  }

  /**
   * Initiate secure verification via email
   */
  static async initiateSecureVerification(
    participantId: string,
    email: string
  ): Promise<ApiResponse> {
    return api.post(API_ENDPOINTS.PARTICIPANTS.SECURE_VERIFY, {
      participantId,
      email,
    });
  }

  /**
   * Secure participant verification - Step 1: Verify personal information
   */
  static async verifyPersonalInformation(
    studyCode: string,
    personalInfo: PersonalInfo
  ): Promise<VerificationSession> {
    const response = await api.post<ApiResponse<{ session: VerificationSession }>>(
      '/api/secure-auth/verify-participant',
      { studyCode, personalInfo }
    );
    return response.data!.session;
  }

  /**
   * Secure participant verification - Step 2: Send email verification
   */
  static async sendEmailVerification(
    sessionId: string,
    email: string
  ): Promise<{ success: boolean }> {
    return api.post('/api/secure-auth/send-email-verification', {
      sessionId,
      email,
    });
  }

  /**
   * Secure participant verification - Step 3: Validate session
   */
  static async validateSession(sessionId: string): Promise<VerificationSession> {
    const response = await api.post<ApiResponse<{ session: VerificationSession }>>(
      '/api/secure-auth/validate-session',
      { sessionId }
    );
    return response.data!.session;
  }

  /**
   * Secure participant verification - Step 4: Complete enrollment
   */
  static async completeSecureEnrollment(
    sessionId: string,
    fitbitData: any
  ): Promise<{ success: boolean; participantId: string }> {
    return api.post('/api/secure-auth/complete-enrollment', {
      sessionId,
      fitbitData,
    });
  }
}

export default ParticipantService;
